import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { getPCA } from '../../app/pca';
import { addAuth, LoginType } from '../../store/slices/function/loginSlice';
import {
  changeFeedbackOption,
  ValidationProblemDetails,
} from '../../store/slices/function/feedback';
import {
  handleSerilogResponse,
  handleValidationProblemDetails,
} from '../feedback';
import type { RootState } from '../../store/store';
import type { Store } from 'redux';

const getStore = (): Store<RootState> => {
  const { store } = require('../../store/store') as { store: Store<RootState> };
  return store;
};

const isSerilogResponse = (obj: any): boolean => {
  return (
    obj &&
    typeof obj === 'object' &&
    ('status' in obj || 'Status' in obj) &&
    ('message' in obj || 'Message' in obj)
  );
};

const handleResponseData = (data: any, api: any): boolean => {
  if (isSerilogResponse(data)) {
    getStore().dispatch(changeFeedbackOption(handleSerilogResponse(data)));
    return true;
  }

  if (
    data &&
    typeof data === 'object' &&
    'data' in data &&
    data.data &&
    isSerilogResponse(data.data)
  ) {
    getStore().dispatch(changeFeedbackOption(handleSerilogResponse(data.data)));
    return true;
  }

  return false;
};

const handleErrorData = (errorData: any, api: any): boolean => {
  if (isSerilogResponse(errorData)) {
    getStore().dispatch(changeFeedbackOption(handleSerilogResponse(errorData)));
    return true;
  } else if (
    errorData &&
    typeof errorData === 'object' &&
    'data' in errorData &&
    errorData.data &&
    isSerilogResponse(errorData.data)
  ) {
    getStore().dispatch(
      changeFeedbackOption(handleSerilogResponse(errorData.data)),
    );
    return true;
  }

  return false;
};

const getBaseQueryWithReauth = (
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async function (args, api, extraOptions) {
    let result = await baseQuery(args, api, extraOptions);
    const pca = getPCA();
    pca.initialize();
    const request = {
      scopes: ['user.read'],
      account: pca.getAllAccounts()[0],
      forceRefresh: true,
      refreshTokenExpirationOffsetSeconds: 1600,
    };

    if (
      result.error &&
      (result.error.status === 401 || result.error.data === 'Unauthorized')
    ) {
      const token = await pca.acquireTokenSilent(request);
      if (token) {
        getStore().dispatch(
          addAuth({
            auth: token.accessToken,
            LoginType: LoginType.backoffice,
            isLogged: true,
            loginResponse: null,
          }),
        );
        result = await baseQuery(args, api, extraOptions);
      }
    }

    if (result.data && handleResponseData(result.data, api)) {
      return result;
    }

    if (
      result.error &&
      result.error.data &&
      handleErrorData(result.error.data, api)
    ) {
      return result;
    }

    //trata as requsicoes 400, invalid request
    if (result.error && result.error.status === 400 && result.error.data) {
      const validationProblemDetails = result.error
        .data as ValidationProblemDetails;
      getStore().dispatch(
        changeFeedbackOption(
          handleValidationProblemDetails(validationProblemDetails),
        ),
      );
    }

    return result;
  };
};

export default getBaseQueryWithReauth;
